import React from 'react';
import { Toast } from 'freemium-ui/lib/types';
import {
  ButtonStyleShapeTypes,
  ButtonStyleTypes,
  CollectorChannelType,
  CollectorStateType,
  ContactFormQuestionType,
  DependentBlockConditionType,
  DependentBlockType,
  DisplayLogicType,
  FileFieldTypes,
  FormFieldType,
  MatrixPropertiesType,
  NumberFieldType,
  PointScaleRatingType,
  PromptChoiceType,
  PromptSettingsType,
  PropertiesLabelsPreferenceType,
  QuestionChoiceType,
  QuestionMetaType,
  QuestionScalePropertiesType,
  QuestionThemeType,
  QuestionType,
  QuestionTypeInfoMetaType,
  QuestionTypeVariantType,
  RangeLabelType,
  ScalePropertiesButtonStyleType,
  ScalePropertiesLabelsType,
  ScalePropertiesOrderType,
  ScalePropertiesRangeType,
  SurveyBlockCategoryType,
  SurveyBlockNavType,
  SurveyBlockType,
  SurveyMetaType,
  SurveyQuestionDetailsType,
  SurveyStateTypes,
  SurveyType,
  SurveyTypes,
  SurveyUiBrandingType,
  SurveyUiThemeType,
  TextFieldType,
} from 'freemium-survey-components/lib/types';
import { History } from 'history';
import * as Yup from 'yup';
import { NoteType } from 'routes/screens/response/types';
import { GlyphType } from './components/Icon';

export type {
  ButtonStyleShapeTypes,
  ButtonStyleTypes,
  CollectorChannelType,
  CollectorStateType,
  DependentBlockConditionType,
  DependentBlockType,
  DisplayLogicType,
  PointScaleRatingType,
  PromptChoiceType,
  PromptSettingsType,
  PropertiesLabelsPreferenceType,
  QuestionChoiceType,
  QuestionScalePropertiesType,
  QuestionThemeType,
  QuestionType,
  QuestionTypeInfoMetaType,
  QuestionTypeVariantType,
  RangeLabelType,
  ScalePropertiesButtonStyleType,
  ScalePropertiesLabelsType,
  ScalePropertiesOrderType,
  ScalePropertiesRangeType,
  SurveyBlockCategoryType,
  SurveyBlockNavType,
  SurveyBlockType,
  SurveyMetaType,
  SurveyQuestionDetailsType,
  SurveyStateTypes,
  SurveyType,
  SurveyTypes,
  SurveyUiBrandingType,
  SurveyUiThemeType,
};

export type SurveyQuestionTypeInfo = {
  comment?: boolean | null;
  driver?: boolean | null;
  question_type?: QuestionType | null;
  question_theme?: QuestionThemeType | null;
  type_variant?: QuestionTypeVariantType | null;
  scale_properties?: QuestionScalePropertiesType | null;
  footer_text?: string | null;
  meta?: QuestionTypeInfoMetaType | null;
  question_comment?: boolean | null;
  question_comment_required?: boolean | null;
  comment_placeholder?: string | null;
  pivot_question?: boolean | null;
  header_text?: string | null;
  matrix_properties?: MatrixPropertiesType | null;
  form_fields?: FormFieldType | null;
  number_field?: NumberFieldType | null;
  file_field?: FileFieldTypes | null;
  text_field?: TextFieldType | null;
  contact_form?: ContactFormQuestionType | null;
};

export type SurveyQuestionType = {
  id?: string | null;
  text?: string | null;
  name: string;
  required?: boolean | null;
  type_info?: SurveyQuestionTypeInfo | null;
  meta?: QuestionMetaType | null;
  dependent_blocks?: DependentBlockType[] | null;
  choices?: QuestionChoiceType[] | null;
  question_weightage?: {
    scoring_enabled: boolean;
    weightage: {
      [key: string]: number;
    };
  } | null;
  tags?: string[];
};

export type FieldTypeIds =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22';

export type FieldTypes =
  | 'text'
  | 'dropdown'
  | 'email'
  | 'phone_number'
  | 'checkbox'
  | 'paragraph'
  | 'date_time'
  | 'number'
  | 'url_set'
  | 'url'
  | 'attachment'
  | 'radio'
  | 'decimal'
  | 'section'
  | 'dependent_dropdown'
  | 'auto_complete'
  | 'date'
  | 'multi_select_dropdown'
  | 'date_time_split'
  | 'big_number'
  | 'group_field'
  | 'multi_select_checkbox';

export type AccountUserType = {
  user: UserType | null;
  active: boolean;
  display_id: string | null;
  freshid_organisation_id: string | null;
  freshid_account_id: string | null;
  freshid_user_id: string | null;
  account_id: string | null;
  user_id: string | null;
  user_role: UserRoleType | null;
  created_by_id: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  meta?: AccountUserMetaType | null;
};

export type DefaultDashboardTypes =
  | 'DEFAULT'
  | 'NPS'
  | 'CSAT'
  | 'CES'
  | 'FREDDY_RESPONSE';

export type AccountUserMetaType = {
  default_dashboard?: DefaultDashboardTypes | null;
  dashboard_dismissed?: boolean | null;
  response_last_exported_at?: string | null;
  test_mail_limit?: string | number | null;
  contacts_last_exported_at?: string | null;
  last_responses_download_at?: string | null;
};

export type AllUsersType = {
  [key in string]: UserType;
};

export type UserType = {
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  locale?: string | null;
  status?: 'INACTIVE' | 'ACTIVATED' | null;
  active?: boolean | null;
  display_id?: string | null;
  freshid_user_id?: string | null;
  freshid_organisation_id?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  signature?: string | null;
  company_name?: string | null;
  activated_at?: number | null;
  weekly_report?: boolean;
  monthly_report?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  last_login?: string | null;
  time_format?: string | null;
};

export type AccountUsersResponseType = {
  account_users: AccountUserType[];
  _links?: LinkMetaType;
};

export type RelatedEntityIdListType = {
  accountContactEntityId?: string | null;
  surveyResponseEntityId?: string | null;
  surveyResponseNoteEntityId?: string | null;
};

export type PrevRelatedEntityIdListType = {
  accountContactEntityId: string | null;
  surveyResponseEntityId: string | null;
  surveyResponseNoteEntityId: string | null;
};

export type AccountMetaType = {
  company_name?: string | null;
  brand_color?: string | null;
  favicon_url?: string | null;
  logo_url?: string | null;
  response_translation_enabled?: boolean | null;
  google_translate_api_key?: string | null;
};

export type SystemMetaType = {
  contact_last_exported_at?: string | null;
  response_last_exported_at?: string | null;
};

export type AccountInfoType = {
  name?: string | null;
  domain?: string | null;
  timezone?: string | null;
  locale?: string | null;
  time_format?: string | null;
  active?: boolean | null;
  id?: string | null;
  freshid_account_id?: string | null;
  freshid_organisation_id?: string | null;
  initial_freshid_account_id?: string | null;
  initial_freshid_organisation_id?: string | null;
  organisation_domain?: string | null;
  region_code?: string | null;
  created_by_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  inactivated_at?: string | null;
  api_key?: string | null;
  mcr_account_id?: string | null;
  contact_form_id?: string | null;
  spam_score?: number | null;
  dkim_verified?: boolean | null;
  dkim_domain?: string | null;
  cname_domain?: string | null;
  custom_domain?: string | null;
  custom_domain_status?: string | null;
  related_entity_id_list?: RelatedEntityIdListType | null;
  prev_related_entity_id_list?: PrevRelatedEntityIdListType | null;
  account_meta?: AccountMetaType | null;
  resource_limit_configuration?: LimitsType | null;
  system_meta?: SystemMetaType | null;
  status?: 'INACTIVE' | 'ACTIVATED' | 'DEACTIVATED' | null;
};

export type CustomDomainDetailsType = {
  id?: string | null;
  organisation_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  custom_domain?: string | null;
  cname_domain?: string | null;
  status?: string | null;
  cert_requested_at?: string | null;
  error?: string | boolean | null;
  error_detail?: string | null;
};

export type MutationType = {
  enabled?: boolean;
  refetchOnMount?: boolean | 'always';
  refetchOnWindowFocus?: boolean;
  setToast?: (t: Toast) => void;
  callback?: (success: boolean, response?: any, payload?: any) => void;
  toggleDrawer?: (open: boolean) => void;
};

export type BreadcrumpDataType = {
  path: string[];
  count?: number;
  text?: string;
};

export type ConfirmationType = {
  id?: string;
  title?: string;
  glyph?: GlyphType;
  data?: any;
  onConfirm?: () => void;
  cancelButtonDisabled?: boolean;
  confirmButtonDisabled?: boolean;
  onDismiss?: () => void;
  fill?: string;
  size?: string;
  height?: string;
  width?: string;
  viewBox?: string;
  infoText?: string;
  className?: 'info' | 'error' | 'warn';
  description?: string | React.ReactElement;
  body?: () => string | React.ReactElement;
  cancelButtonText?: string;
  confirmButtonText?: string;
};

export type ConditionType = {
  id?: string;
  name: string;
  evaluate_on: string;
  operator: FilterOperatorsType | '';
  field_type: string;
  value?: any;
};

export type FilterFieldsType =
  | 'date_time'
  | 'date'
  | 'decimal'
  | 'number'
  | 'radio'
  | 'text'
  | 'email'
  | 'paragraph'
  | 'url'
  | 'phone_number'
  | 'dropdown'
  | 'auto_complete'
  | 'multi_select_checkbox'
  | 'multi_select_dropdown'
  | 'group_field'
  | 'big_number';

export type FilterOperatorsType =
  | 'CHECKED'
  | 'NOT_CHECKED'
  | 'IS_NOT_NULL'
  | 'IS_NULL'
  | 'IS'
  | 'IS_EMPTY'
  | 'IS_NOT_EMPTY'
  | 'BETWEEN'
  | 'IS_NOT'
  | 'AFTER'
  | 'BEFORE'
  | 'BEFORE_NTH_DAY'
  | 'BEFORE_NTH_MINS'
  | 'CONTAINS'
  | 'DOES_NOT_CONTAINS'
  | 'ENDS_WITH'
  | 'GREATER_THAN'
  | 'GREATER_THAN_OR_EQUALS'
  | 'IN'
  | 'LAST_MONTH'
  | 'LAST_N_DAYS'
  | 'LAST_N_DAYS_INCLUDING_TODAY'
  | 'LAST_NTH_MINS'
  | 'LAST_WEEK'
  | 'LESS_THAN'
  | 'LESS_THAN_OR_EQUALS'
  | 'NEXT_N_DAYS'
  | 'NEXT_N_DAYS_INCLUDING_TODAY'
  | 'NOT_IN'
  | 'ON'
  | 'ON_NTH_DAY'
  | 'ON_NTH_MINS'
  | 'STARTS_WITH'
  | 'THIS_MONTH'
  | 'THIS_WEEK'
  | 'TODAY'
  | 'TOMORROW'
  | 'YESTERDAY'
  | 'IS_EMPTY_OR_NULL'
  | 'IS_NOT_EMPTY_AND_NOT_NULL';

export type FieldGroupsType = {
  id: string;
  name: string;
  filter: (
    value: FormServFieldType,
    index: number,
    array: FormServFieldType[],
  ) => boolean;
};

export type TimeZone = {
  offset: string;
  timeZone: string;
};

export type OptionType = {
  id: string;
  label: string;
};

export type ConditionsType = {
  id: string;
  name: string;
  value: any;
  type?: string;
  isConditional?: boolean;
  conditionType?: string;
};

export type FilterOperationType = {
  value: string;
  id: FilterOperatorsType;
  inputValueType?: 1 | 7 | 17 | 8 | 18;
  overrideFieldType?: boolean;
  between?: boolean;
  needValue?: boolean;
};

export type FilterOperationsType = {
  [key in FilterOperatorsType]: FilterOperationType;
};

export type CustomTableColumnType = {
  disableReordering?: boolean;
  type?: number;
  filterable?: boolean;
  showInitial?: boolean;
  accessor?: string | ((data: any) => void);
  validation?: Yup.AnySchema;
};

export type ColumnSettingsType = {
  [key in keyof McrContactType]?: CustomTableColumnType;
} & {
  [key: string]: CustomTableColumnType;
};

export type FieldOptionsType = {
  option_label_path?: string | null;
  option_value_path?: string | null;
  hidden?: boolean | null;
  allow_clear?: boolean | null;
  min_search_char_length?: number | null;
  creatable?: number | null;
  creatable_data_key?: string | null;
  options_component?: string | null;
  lookup_prefetch_url?: string | null;
  selected_item_component?: string | null;
  autoclose?: boolean | null;
  calendar_weeks?: boolean | null;
  clear_btn?: boolean | null;
  dates_disabled?: string | null;
  days_of_week_disabled?: string | null;
  end_date?: string | null;
  force_parse?: boolean | null;
  format?: string | null;
  keyboard_navigation?: boolean | null;
  language?: string | null;
  min_view_mode?: string | null;
  orientation?: string | null;
  start_date?: string | null;
  start_view?: string | null;
  today_btn?: boolean | null;
  today_highlight?: boolean | null;
  week_start?: number | null;
  first_option?: string | null;
  last_option?: string | null;
  step?: string | null;
  twenty_four_hr_format?: boolean | null;
  choice_mapping_attr?: string | null;
  locale?: string | null;
  fs_default?: boolean | null;
  unique?: boolean | null;
  filterable?: boolean | null;
  allowClear?: boolean | null;
};

export type FieldChoicesType = {
  id: string;
  account_id?: string;
  value?: string;
  internal_name?: string | null;
  field_id?: string;
  editable?: boolean;
  custom?: boolean;
  position?: number;
  dependent_ids?: any | null;
};

// https://confluence.freshworks.com/display/PLAT/Field+Metadata

export type FormServFieldType = {
  id: string;
  name: string;
  type: number;
  label?: string;
  position?: number;
  visible?: boolean;
  required?: boolean;
  editable?: boolean;
  deleted?: boolean;
  internal?: boolean;
  builder?: boolean;
  custom?: boolean;
  validatable?: boolean;
  derived?: boolean;
  placeholder?: string;
  hint?: string;
  field_options?: FieldOptionsType;
  regex?: {
    validation?: string;
    message?: string;
  };
  fields?: any[];
  choices?: FieldChoicesType[] | null;
  account_id?: number;
  form_id?: number;
  default_value?: any;
  column_type_blob?: boolean;
  nodegroup_skip?: boolean;
  field_class?: string;
  created_at?: string;
  updated_at?: string;
};

export type CustomFieldType = {
  id: string;
  glyph: GlyphType;
  limit: number;
  type: number;
  name: string;
  width: string;
  height: string;
  metaData?: Partial<FormServFieldType>;
};

// https://confluence.freshworks.com/display/PLAT/Form+Metadata

export type ContactFormType = {
  form_id?: number;
  account_id?: number;
  coll_id?: string;
  prod_id?: string;
  organisation_id?: number;
  created_at?: string;
  updated_at?: string;
  form_options?: any;
  docId?: string;
  name?: string;
  title?: string;
  description?: string;
  deleted?: boolean;
  active?: boolean;
  compressed?: boolean;
  version?: number;
  avatars?: any;
  fields?: FormServFieldType[];
};

export type GroupFieldType = {
  labelId?: string;
  primary?: string;
  value?: any;
};

export type TagListsResponseType = {
  accountTags?: TagListType[] | null;
  _links?: LinkMetaType | null;
  metadata?: any;
};

export type TagListType = {
  id?: string | null;
  name?: string | null | any;
  account_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted?: boolean | null;
  tag_response_count?: number;
  case_insensitive_name?: string | null;
};

export type ContactListsResponseType = {
  contactList?: ContactListType[] | null;
  _links?: LinkMetaType;
};

export type ContactListType = {
  account_id?: string | null;
  contacts_count?: number | null;
  created_at?: string | null;
  created_by_id?: string | null;
  description?: string | null;
  display_id?: string | null;
  label?: string | null;
  updated_at?: string | null;
  deleted?: boolean;
  name?: string | null;
};

export type MatchTypes = 'ALL' | 'ANY';

export type ConditionSetType = {
  id?: string;
  match_type: MatchTypes;
  conditions: ConditionType[];
};

export type RuleType = {
  name?: string;
  version?: string;
  id?: string;
  match_type: MatchTypes;
  condition_sets: ConditionSetType[];
};

export type McrContactType = {
  id?: string | null;
  account_id?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  creater_id?: string | null;
  updater_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  mobile_number?: string | null;
  primary_email?: string | null;
  sub_state?: number;
  status?: number;
  version?: string | null;
  twitter?: string | null;
  zipcode?: string | null;
  visitor_id?: string | null;
  work_email?: string | null;
  work_number?: string | null;
  deleted?: boolean;
  fields?: {
    [key: string]: any;
  };
  external_id?: string | null;
  emails?: GroupFieldType[];
  phone_numbers?: GroupFieldType[];
  mcr_company_ids?: GroupFieldType[];
  created_at?: string | null;
  updated_at?: string | null;
  created_by_product?: string | null;
  updated_by_product?: string | null;
  system_created_at?: string | null;
  merged_to?: string | null;
  merged_from?: string | null;
  id_str?: string | null;
  persona?: string | null;
  primary_company_name?: string | null;
  last_seen?: string | null;
};

export type SegmentsResponseType = {
  page_size?: number | null;
  page_token?: string | null;
  links?: LinkMetaType;
  segment_rules?: SegmentType[] | null;
};

export type SegmentType = {
  id?: string;
  name?: string;
  version?: string;
  entity?: 'CONTACT';
  rule?: RuleType;
  contacts_count?: number;
  account_id?: string;
  creator_id?: string;
  updater_id?: string;
  created_at?: string;
  updated_at?: string;
  segment_type?: 'RULE_BASED';
};

export type LinkMetaType = {
  next?: string | null | { href: string };
  prev?: string | null | { href: string };
  count?: number | null | { href: string };
  self?: number | null | { href: string };
};

export type MetaType = {
  totalNumberOfRecords?: number;
  totalNumberOfPages?: number;
  pageNumber?: number;
  pageSize?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
};

export type SegmentResponseType = {
  contacts: McrContactType[];
  meta: MetaType;
};

export type ContactLimitType = {
  frequency_month?: number | null;
  frequency_week?: number | null;
  frequency_quarter?: number | null;
};

export type ContactSettingsType = {
  contact_limit?: ContactLimitType;
  recipient_list_in_hours?: number | string | null;
};

export type SurveyLimitInfoType = {
  surveys_per_month?: number | null;
  surveys_per_quarter?: number | null;
  surveys_per_semi_annual?: number | null;
  surveys_per_year?: number | null;
};

export type TenantInfoAccountMetaType = {
  is_question_translation_enabled?: string | null;
  dkim_verified?: boolean | 'true' | 'false' | null;
  enabled_features?: string[] | null;
};

export type TenantLimitConfig = {
  no_of_saved_templates?: number | null;
};

export type TenantInfoType = {
  id?: string | null;
  external_account_id?: string | null;
  external_organization_id?: string | null;
  survey_limit_info?: SurveyLimitInfoType | null;
  contact_settings?: ContactSettingsType | null;
  active?: boolean | null;
  custom_domain?: string | null;
  account_meta?: TenantInfoAccountMetaType | null;
  created_at?: string | null;
  updated_at?: string | null;
  unsubscribe_url?: string | null;
  secret_key?: string | null;
  limit_config?: TenantLimitConfig | null;
};

export type UiThemeType = {
  body_background_color?: string | null;
  primary_font?: string | null;
  secondary_font?: string | null;
  primary_button_color?: string | null;
  secondary_button_color?: string | null;
  brand_color?: string | null;
  primary_button_text_color?: string | null;
  button_style?: string | null;
  primary_background_color?: string | null;
  secondary_background_color?: string | null;
  secondary_button_text_color?: string | null;
  question_background_color?: string | null;
  question_text_color?: string | null;
  css_url?: string | null;
};

export type UiBrandingType = {
  favicon_url: string | null;
  logo_url: string | null;
  name: string | null;
};

export type LinearScaleType = {
  point_scale?: string | null;
  scale_order?: string | null;
  button_style?: string | null;
  button_shape?: 'rounded' | 'square' | 'curved';
};

export type PreferredTimeType = {
  hour?: number | null;
  minute?: number | null;
  survey_trigger_timezone?: string | null;
};

type NumberOrNull = number | null;

export type DelayTypes = 'before' | 'after';

export type SurveyDelayInfoType = {
  initial_survey_delay_by_days?: number | null;
  based_on?: string | null;
  delay_type?: DelayTypes | null;
};

export type SurveyCutoffType = {
  cutoff_after_responses_count?: number | null;
};

export type ScheduleInfoType = {
  start_date?: string | null;
  end_date?: string | null;
  preferred_time?: PreferredTimeType | null;
  remind_in?: NumberOrNull[] | null;
  next_survey_when_responded?: number | null;
  next_survey_when_not_responded?: number | null;
  survey_delay_info?: SurveyDelayInfoType | null;
  survey_cutoff?: SurveyCutoffType | null;
  survey_link_expiry_in_days?: number | null;
};

export type ThrottleInfoType = {
  limit_per_day?: number | null;
  web_in_app_limit_per_day?: number | null;
  limit_per_month?: number | null;
  web_in_app_limit_per_month?: number | null;
};

export type TemplateScoreType = {
  spam_score?: number | null;
  required_score?: number | null;
  spam?: boolean | null;
};

export type EmailChannelInfoType = {
  dkim_settings?: any | null;
  from_name?: string | null;
  from_email?: string | null;
  subject?: string | null;
  reply_to?: string | null;
  reply_to_name?: string | null;
  preheader?: string | null;
  email_body?: string | null;
  footer?: string | null;
  unsubscribe?: string | null;
  email_body_bottom?: string | null;
  copyright?: string | null;
  include_unsubscribe_link?: boolean | null;
  template_score?: TemplateScoreType | null;
};

export type WidgetStyles = 'default' | 'compact' | 'cozy';

export type WebAppChannelInfoType = {
  widget_style?: WidgetStyles | null;
  page_load_delay?: number | null;
  include_unsubscribe?: boolean;
  // is_emailID_hash_enabled?: boolean;
};

export type MarketingChannelsTypes =
  | 'Freshmarketer'
  | 'Freshworks CRM'
  | 'Freshdesk Customer Success'
  | 'ActiveCampaign'
  | 'Autopilot'
  | 'Customer.io'
  | 'Campaign Monitor'
  | 'Drip'
  | 'HubSpot'
  | 'Klaviyo'
  | 'MailChimp'
  | 'Mailjet'
  | 'Sendinblue'
  | 'SendGrid'
  | 'Zoho Campaigns'
  | 'Stripo'
  | 'Intercom'
  | 'Calendly'
  | 'Marketo'
  | 'Pardot'
  | 'Zendesk'
  | 'Sharpspring'
  | 'Keap'
  | 'Convertkit'
  | 'ConstantContact'
  | 'GetResponse'
  | 'Mailgun'
  | 'Helpscout'
  | 'Vero'
  | 'Marketing Cloud'
  | 'Drift'
  | 'Boomtrain'
  | 'Bronto'
  | 'Salesmanago'
  | 'Lifecycle.io'
  | 'Braze'
  | 'Others';

export type MarketingChannelPlaceholdersType = {
  work_email: string;
  first_name: string;
  last_name: string;
  work_number?: string;
};

export type EmailEmbedChannelInfoType = {
  marketing_channel: MarketingChannelsTypes;
  show_footer?: boolean | null;
  align_center?: boolean | null;
  contact_fields: Array<{
    name: string;
    value: string;
  }>;
};

export type ShareContactFieldType = {
  name: string;
  value: string;
};

export type WebLinkChannelInfoType = {
  contact_fields?: ShareContactFieldType[];
};

export type ChannelInfoType = {
  email_channel_info?: EmailChannelInfoType | null;
  web_in_app_channel_info?: WebAppChannelInfoType | null;
  email_embed_channel_info?: EmailEmbedChannelInfoType | null;
  web_link_channel_info?: WebLinkChannelInfoType | null;
};

export type SurveysResponseType = {
  surveys?: SurveyType[];
  _links?: LinkMetaType;
  metadata?: any;
};

export type ActionType = {
  hideToast?: boolean;
};
export type SurveyResponseDetailedDataType = {
  name?: string | null;
  question?: string | null;
  answer?: any | null;
  others_answer?: any | null;
  labels?: string[] | null;
  score?: {
    given_score: number;
    max_score: number;
  };
};

export type ResponseStatusType = 'PARTIAL' | 'COMPLETE' | 'PARTIAL_COMPLETE';

export type PageDataType = {
  description?: string | null;
  name: string | null;
  page_id: string | null;
  question_ids: string[] | null;
  title: string | null;
  order?: number | null;
};

export type SurveyResponseType = {
  status: ResponseStatusType;
  channel?: CollectorChannelType;
  comment?: string | null;
  url?: string | null;
  contact_first_name?: string | null;
  contact_id?: string | null;
  contact_last_name?: string | null;
  contact_email?: string | null;
  contact_lists?: string[] | null;
  contact_segments?: string[] | null;
  created_at?: string | null;
  surveyserv_survey_event_id?: string | null;
  id: string;
  invalid_reason?: string | null;
  prev_response_id?: string | null;
  surveyserv_collector_id?: string | null;
  transaction_id?: string | null;
  language?: string | null;
  is_active?: boolean | null;
  deleted?: boolean | null;
  is_edited?: boolean | null;
  is_editable?: boolean | null;
  commentPresent?: boolean | null;
  is_favourite?: boolean | null;
  is_invalid?: boolean | null;
  v1Deleted?: boolean | null;
  is_non_actionable?: boolean | null;
  question_answers?: any | null;
  nps_rating?: number | null;
  csat_rating?: number | null;
  ces_rating?: number | null;
  nps_rating_group?: string | null;
  csat_rating_group?: string | null;
  ces_rating_group?: string | null;
  summary?: string | null;
  survey_collector_id?: string | null;
  survey_id?: string | null;
  surveyserv_response_id: string;
  tags?: any | null;
  trigger_id?: string | null;
  ucr_contact_id?: string | null;
  updated_at?: string | null;
  rating_group?: string | null;
  rating?: number | null;
  prev_rating?: number | null;
  prev_rating_group?: string | null;
  prev_nps_rating?: string | null;
  prev_csat_rating?: string | null;
  prev_ces_rating?: string | null;
  prev_nps_rating_group?: string | null;
  prev_csat_rating_group?: string | null;
  prev_ces_rating_group?: string | null;
  detailed_data?: SurveyResponseDetailedDataType[] | null;
  detailed_response_score?: ResponseScoreType | null;
  page_data?: PageDataType[];
  freddy_response_analysis?: FreddyResponseType | null;
  survey_version_id?: string | null;
};

type FreddyResponseType = {
  customer_sentiment: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
  suggested_tags: FreddyTagsType | null;
};
export type FreddyTagsType = {
  negative: string[] | null;
  neutral: string[] | null;
  positive: string[] | null;
};

export type ResponseScoreType = {
  cumulative_max_score: number;
  cumulative_given_score: number;
  scoring_group: string;
  score_percent: number;
};

export type SurveysResponsesResponseType = {
  surveyResponses?: SurveyResponseType[];
  _links?: LinkMetaType;
};

export type FilterViewType = {
  id?: string;
  conditions: ConditionType[];
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type FilterViewList = {
  filter_view_list: FilterViewType[];
  _links: LinkMetaType;
};

export type GoToRouteType = (params: {
  type?: string;
  path: string;
  history?: History;
}) => void;

export type PlaceholderOptionType = {
  id: string;
  name: string;
  placeholder: string;
};

export type PlaceholderType = {
  id: string;
  label: string;
  options: PlaceholderOptionType[];
};

export type PlaceholderConfig = {
  isAddSpaceAtEnd?: boolean;
};

export type CountResponseType = {
  count?: string | null;
  measure?: string | null;
};

export type DkimType = {
  host?: string;
  verified?: boolean;
  type?: string;
  reason?: string;
  value?: string;
};

export type DkimListType = {
  RequestId?: string;
  accountId?: number;
  default?: boolean;
  domain?: string;
  id?: number;
  records?: {
    dkim?: DkimType[];
    spfmx?: DkimType;
  };
  subdomain?: string;
  verified?: boolean;
};

export type LoginRquestType = {
  code?: string;
  domain?: string;
};

export type LoginResponseType = {
  redirect_url?: string | null;
  token?: string | null;
  non_field_errors?: string | null;
};

type Navigation = {
  navigation: {
    IMPORT_CONTACTS?: string;
    ADD_CONTACT_SEGMENT: string;
    ADD_CONTACT_LIST: string;
    DKIM_SETTINGS: string;
    ADD_CONTACT: string;
    TRANSLATION_SETTINGS: string;
  };
};

type ApiHost = { API_HOST: string };

export type LimitsType = {
  active_surveys?: number;
  surveys?: number;
  workflows?: number;
  users?: number;
  survey_collectors?: number;
  contacts?: number;
  contact_lists?: number;
  segments?: number;
  templates?: number;
  survey_blocks?: number;
  no_of_active_surveys?: number;
  no_of_survey_collectors?: number;
  no_of_surveys?: number;
  dkim_limit?: number;
  no_of_saved_templates?: number;
};

export type HostConfigType = Partial<Navigation & ApiHost> & {
  limits?: LimitsType;
};

export type UserRoleType = 'OWNER' | 'ANALYST' | 'VIEWER' | 'ADMIN';

export type AccountInviteType = {
  display_id: string | null;
  account_id: string | null;
  email: string | null;
  token: string | null;
  invited_by: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  expiry?: number | null;
  user_role: UserRoleType | null;
  active: boolean | null;
  weekly_report: boolean | null;
  monthly_report: boolean | null;
};

export type AccountInvitesResponseType = {
  account_invites?: AccountInviteType[] | null;
};

export type PlaceholderValuesType = {
  placeholders?: Record<string, string> | null;
};

export type WorkflowConditionOperators =
  | 'IS'
  | 'IS_NOT'
  | 'CONTAINS'
  | 'DOES_NOT_CONTAINS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'IS_EMPTY'
  | 'IS_NOT_EMPTY'
  | 'GREATER_THAN'
  | 'LESSER_THAN'
  | 'GREATER_THAN_OR_EQUALS'
  | 'LESSER_THAN_OR_EQUALS'
  | 'BEFORE'
  | 'AFTER'
  | 'BETWEEN';

export type WorkflowConditionVariables =
  | 'NPS_RATING'
  | 'CSAT_RATING'
  | 'CES_RATING'
  | 'NPS_RATING_GROUP'
  | 'CSAT_RATING_GROUP'
  | 'CES_RATING_GROUP'
  | 'PREVIOUS_NPS_RATING'
  | 'PREVIOUS_CSAT_RATING'
  | 'PREVIOUS_CES_RATING'
  | 'PREVIOUS_NPS_RATING_GROUP'
  | 'PREVIOUS_CSAT_RATING_GROUP'
  | 'PREVIOUS_CES_RATING_GROUP'
  | 'SURVEY_ID'
  | 'CHANNEL'
  | 'SEGMENT'
  | 'LIST'
  | 'QUESTION'
  | 'CONTACT_FIELD';

export type WorkflowConditionVariableTypes = 'RATING' | 'RATING_GROUP';

export type WorkflowSubConditionVariableOptions =
  | 'STATEMENT'
  | 'FORM_FIELD'
  | 'CONTACT_FORM';

export type WorkflowSubConditionType = {
  variable_id?: string | null;
  variable?: WorkflowSubConditionVariableOptions | null;
  deleted?: boolean | null;
  value?: string[] | null;
};

export type WorkflowConditionType = {
  id?: string | null;
  name?: string | null;
  condition_group_id?: string | null;
  operator?: WorkflowConditionOperators | null;
  variable?: WorkflowConditionVariables | null;
  value?: string | null;
  deleted?: boolean | null;
  order?: number | null;
  variable_id?: string | null;
  variable_type?: WorkflowConditionVariableTypes | null;
  rank_order_choice_id?: string | null;
  sub_condition?: WorkflowSubConditionType[] | null;
};

export type WorkflowConditionGroupType = {
  id?: string | null;
  workflow_id?: string | null;
  name?: string | null;
  deleted?: boolean | null;
  order?: number | null;
  conditions_operator?: WorkflowConditionOptions | null;
  conditions?: WorkflowConditionType[] | null;
};

export type WorkContactFormFieldType = {
  id: string;
  label: string | undefined;
  type: string;
  choices: FieldChoicesType[] | null | undefined;
};

export type WorkflowActionDelayTypes = 'Days' | 'Hours' | 'Minutes';

export type WorkflowActionContactFieldType = {
  field?: string | null;
  value?: any | null;
};

export type WorkflowAuthenticationTypes = 'None' | 'basic_auth' | 'api_key';

export type WorkflowBasicAuthType = {
  user?: string | null;
  pass?: string | null;
};

export type WorkflowCustomHeaderType = {
  key?: string | null;
  value?: string | null;
};

export type WorkflowActionMetaType = {
  authentication?: WorkflowAuthenticationTypes | null;
  api_key?: WorkflowCustomHeaderType | null;
  basic_auth?: WorkflowBasicAuthType | null;
  custom_headers?: WorkflowCustomHeaderType[] | null;
  payload?: string | null;
  url?: string | null;
  body?: string | null;
  delay?: number | null;
  delay_type?: WorkflowActionDelayTypes | null;
  from_mail?: string | null;
  from_name?: string | null;
  reply_to?: string | null;
  subject?: string | null;
  contact_fields?: WorkflowActionContactFieldType[] | null;
  to_mail?: string[] | null;
  tags?: string[] | null;
};

export type WorkFlowActionTypes =
  | 'SEND_AN_EMAIL'
  | 'SEND_AN_EMAIL_TO_RESPONDENT'
  | 'WEBHOOK'
  | 'MARK_AS_FAVOURITE'
  | 'MARK_AS_INVALID'
  | 'MARK_AS_NON-ACTIONABLE'
  | 'ADD_TAGS'
  | 'UPDATE_A_CONTACT';

export type WorkflowActionType = {
  id?: string | null;
  workflow_id?: string | null;
  action_type?: WorkFlowActionTypes | null;
  action_meta?: WorkflowActionMetaType | null;
  deleted?: boolean | null;
  order?: number | null;
};

export type DateTimeOptions = {
  id?: string | null;
  format?: string | null;
  label?: string | null;
  AmPmFormat?: string | null;
  type?: 'DATE' | 'DATE_TIME';
};

export type WorkflowTypeOptions = 'AUTOMATIC' | 'MANUAL';
export type WorkflowConditionOptions = 'AND' | 'OR';

export type WorkflowEventTypes =
  | 'EVERYTIME_A_SURVEY_IS_RESPONDED'
  | 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED'
  | 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED';

export type WorkflowType = {
  workflow_id?: string | null;
  name?: string | null;
  account_id?: string | null;
  type?: WorkflowTypeOptions | null;
  event?: WorkflowEventTypes | null;
  created_by_id?: string | null;
  updated_by_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  order?: number | null;
  active?: boolean | null;
  enabled?: boolean | null;
  deleted?: boolean | null;
  survey_id?: string | null;
  condition_groups_operator?: WorkflowConditionOptions | null;
  condition_groups?: WorkflowConditionGroupType[] | null;
  actions?: WorkflowActionType[] | null;
};

export type AccountSubdomainResponseType = {
  available?: boolean;
};

export type AccountSubdomainType = {
  subdomain?: string;
};
export type BulkUpdatePayloadType = {
  type: 'UPDATE' | 'DELETE';
  rule?: RuleType | null;
  contact_ids?: string[] | null;
  fields: Record<string, any>;
  callback?: (isSuccess: boolean, response: any) => void;
};

export type AssociationResponse = Record<string, CollectorChannelType[]>;

export type SaveContactFormFieldType = ActionType & {
  payload: FormServFieldType;
  id?: string;
};

export type FieldsType = {
  id: string;
  name: string;
  type?: string;
  label?: string;
  required?: boolean;
  visible?: boolean;
};

export type NotesResponseType = {
  responseNotes?: NoteType[];
  _links?: LinkMetaType;
};

export type SurveyCountResponseType = {
  count?: string | null;
  measure?: string | null;
};

export type ContactFieldsType = {
  fields?: FieldsType[];
};

export type WorkflowsResponseType = {
  workflows?: WorkflowType[];
  _links?: LinkMetaType;
};

export type SurveyServAuthType = {
  jwt_token: string;
  account: {
    id?: string | null;
    timezone?: string | null;
    company_name?: string | null;
    brand_color?: string | null;
    logo_url?: string | null;
    favicon_url?: string | null;
    verified?: boolean | null;
    dkim_domain?: string | null;
    dkim_verified?: boolean | null;
  };
  user: {
    id?: string | null;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    verified?: boolean | null;
    locale?: string | null;
  };
};

export type TransactionFieldType = {
  deleted?: boolean;
  filterable?: boolean;
  id: string;
  label: string;
  name: string;
  searchable?: boolean;
  type: string;
};

export type TransactionFieldsType = {
  fields?: TransactionFieldType[];
};

export type SurveyBlockWithQuestionType = SurveyBlockType & {
  question?: SurveyQuestionType;
};

export type TranslationQuestionType = {
  name?: string;
  text?: string;
  choices?: {
    [key in string]: string;
  };
  labels?: string[] | null;
  declined_message?: string;
  header_text?: string;
  footer_text?: string;
};
export type EmailVerifyResponseType = {
  email?: string | null;
  verified?: boolean | null;
  id?: string | null;
  token?: string | null;
  expiry?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  account_id?: string | null;
  is_verified?: boolean | null;
};

export type MatrixValueType = {
  statement_id?: string;
  rating_labels?: {
    id?: string;
    text?: string;
  }[];
}[];

export type FwaaDebugType = {
  log?: boolean;
};

export type QueueFilterType = {
  localState: any;
  isApply: boolean;
  filterParams: any;
};

export type LimitResetValueType = {
  limit_reset_on?: string;
};

export type TimeUnit =
  | 'frequency_week'
  | 'frequency_month'
  | 'frequency_quarter'
  | 'surveys_per_month'
  | 'surveys_per_quarter'
  | 'surveys_per_semi_annual'
  | 'surveys_per_year';

export type TimeUnitType = {
  id: TimeUnit;
  singleName: string;
};

export const timeUnits: TimeUnitType[] = [
  {
    id: 'frequency_week',
    singleName: 'settings.global_survey_limit.time_units.week',
  },
  {
    id: 'frequency_month',
    singleName: 'settings.global_survey_limit.time_units.month',
  },
  {
    id: 'frequency_quarter',
    singleName: 'settings.global_survey_limit.time_units.quarter',
  },
];

export const surveyLimitTimeUnits: TimeUnitType[] = [
  {
    id: 'surveys_per_month',
    singleName: 'settings.global_survey_limit.survey_limit_time_units.month',
  },
  {
    id: 'surveys_per_quarter',
    singleName: 'settings.global_survey_limit.survey_limit_time_units.quarter',
  },
  {
    id: 'surveys_per_semi_annual',
    singleName:
      'settings.global_survey_limit.survey_limit_time_units.semi-annual',
  },
  {
    id: 'surveys_per_year',
    singleName: 'settings.global_survey_limit.survey_limit_time_units.year',
  },
];
